import Hls from 'hls.js'
import { Options } from 'plyr'
import { APITypes } from 'plyr-react'
import 'plyr-react/plyr.css'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Plyr } from '~/components/atoms/Plyr'
import { useVideoLanguage } from '~/hooks/useGetVideoLanguage'
import browserEnv from '~/libs/env/browserEnv'
import { useVideoDetail } from '../../hooks/useVideoDetail'

const TrailerPlayer: FC<{ id: string }> = ({ id }) => {
  const { data } = useVideoDetail(id)
  const video = data?.originalVideo?.trailer
  const videoElemId = 'player-video-trailer'
  const lambda = browserEnv.NEXT_PUBLIC_PLAYER_PROXY_LAMBDA

  const ref = useRef<APITypes>(null)
  const hls = useMemo(() => new Hls(), [])
  const isHlsSupported = useMemo(() => Hls.isSupported(), [])
  const hasQuality = useRef<boolean>(false)

  const [plyrOptions, setPlyrOptions] = useState<Options | undefined>({
    muted: false,
    autoplay: true,
    keyboard: {
      global: true,
    },
    tooltips: {
      controls: true,
      seek: true,
    },
    controls: [
      'play-large', // The large play button in the center
      'rewind', // Rewind by the seek time (default 10 seconds)
      'play', // Play/pause playback
      'fast-forward', // Fast forward by the seek time (default 10 seconds)
      'progress', // The progress bar and scrubber for playback and buffering
      'current-time', // The current time of playback
      'duration', // The full duration of the media
      'mute', // Toggle mute
      'volume', // Volume control
      'captions', // Toggle captions
      'settings', // Settings menu
      'pip', // Picture-in-picture (currently Safari only)
      'airplay', // Airplay (currently Safari only)
      'fullscreen', // Toggle fullscreen
    ],
  })

  const { getVideoLanguage, updateVideoLanguage } = useVideoLanguage()

  const usePlyr = useMemo(
    () => isHlsSupported && browserEnv.NEXT_PUBLIC_ENV === 'production',
    [isHlsSupported],
  )

  useEffect(() => {
    if (video == undefined) return
    const videoElem = document.getElementById(videoElemId) as HTMLVideoElement
    const src = video.fileUrl ?? ''
    if (isHlsSupported && !isMobile) {
      hls.detachMedia()
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (hasQuality.current) return // early quit if already set

          const levels = hls.levels
          const quality: Options['quality'] = {
            default: levels[levels.length - 1].height,
            options: levels.map((level) => level.height),
            forced: true,
            onChange: (newQuality: number) => {
              levels.forEach((level, levelIndex) => {
                if (level.height === newQuality) {
                  hls.currentLevel = levelIndex
                }
              })
            },
          }
          setPlyrOptions({ ...plyrOptions, quality })
          hasQuality.current = true
        })
      })
      hls.attachMedia(videoElem)
    } else if (videoElem.canPlayType('application/vnd.apple.mpegurl')) {
      // 予告の動画はアクセス制限がないのでラムダ経由じゃなくていい
      videoElem.src = src
      videoElem.querySelectorAll('track')?.forEach((e) => {
        e.remove()
      })

      videoElem.setAttribute('playsInline', 'true')
      videoElem.load()
    } else {
      console.error(
        'This is an old browser that does not support MSE https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API',
      )
    }

    const videoLanguage = getVideoLanguage(video.vtts ?? [])

    video.vtts?.forEach((subtitle, index) => {
      const id = `track-${subtitle.locale}`
      if (!document.getElementById(id)) {
        const elem = document.createElement('track') as HTMLTrackElement
        elem.id = id
        elem.kind = 'captions'
        const path = new URL(subtitle.url).pathname
        elem.src = `/proxy_resource${path}?v=${Date.now()}`
        elem.srclang = subtitle.locale
        elem.label = subtitle.label
        elem.default = subtitle.default ?? false
        if (usePlyr) {
          elem.oncuechange = () => {
            if (ref.current?.plyr.currentTrack !== undefined) {
              if (ref.current.plyr.currentTrack === -1) {
                updateVideoLanguage('disabled')
              }

              if (ref.current.plyr.currentTrack === index) {
                updateVideoLanguage(subtitle.locale)
              }
            }
          }
        }
        videoElem.appendChild(elem)
      }
    })

    if (!usePlyr) {
      Array.from(videoElem.textTracks).forEach((track) => {
        track.mode = track.language === videoLanguage ? 'showing' : 'hidden'
      })
      videoElem.textTracks.onchange = () => {
        const activeTrack = Array.from(videoElem.textTracks).find(
          (track) => track.mode === 'showing',
        )
        updateVideoLanguage(activeTrack?.language ?? 'disabled')
      }
    }
  }, [
    video,
    usePlyr,
    videoElemId,
    plyrOptions,
    hls,
    getVideoLanguage,
    isHlsSupported,
    updateVideoLanguage,
  ])

  return (
    <div>
      {usePlyr ? (
        <Plyr ref={ref} plyrOptions={plyrOptions} id={videoElemId} />
      ) : (
        <video id={videoElemId} style={{ width: '100%' }} controls autoPlay />
      )}
    </div>
  )
}

TrailerPlayer.displayName = 'TrailerPlayer'

export default TrailerPlayer
